import React, { useCallback, useEffect, useRef } from 'react';
import { useHistory, useTranslation } from '@shared/core';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { WeddingCodeBanner } from './WeddingCodeBanner';
import { WeddingCodeBannerSlot } from './WeddingCodeBannerSlot';
import { useWeddingCodeBannerEligibility } from './useWeddingCodeBannerEligibility';

export type WeddingCodeBannerContainerProps = Readonly<{
  eventHandle: string;
  event: GuestSiteEvent;
}>;

/**
 * Connects the presentational {@link WeddingCodeBanner} to guest-site data: it resolves per-guest
 * eligibility, builds the localized copy with the code interpolated, fires impression/click
 * telemetry, and navigates to accommodations.
 *
 * Renders nothing until eligibility is confirmed, then mounts {@link WeddingCodeBannerSlot} and the
 * banner together: the slot takes the banner's height in flow and the `position: fixed` banner lands
 * on it, so the banner never covers content. Reserving the space speculatively (before the
 * eligibility reads land) is deliberately avoided — on ineligible events it painted a visible strip
 * that then collapsed (PRNT-4836 follow-up).
 */
export const WeddingCodeBannerContainer: React.FC<WeddingCodeBannerContainerProps> = ({ eventHandle, event }) => {
  const { tq } = useTranslation('guestSiteTravel');
  const telemetry = useGuestSiteTelemetry();
  const history = useHistory();

  const weddingCode = event.info.weddingCode;
  const { isEligible, accommodationsPath } = useWeddingCodeBannerEligibility({
    eventId: event.id,
    eventHandle,
    weddingCode,
  });

  // Fire the impression once, the first time the banner becomes eligible to render.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (isEligible && weddingCode && !hasTrackedView.current) {
      hasTrackedView.current = true;
      telemetry.weddingCodeBannerViewed(weddingCode);
    }
  }, [isEligible, weddingCode, telemetry]);

  const handleNavigate = useCallback(() => {
    if (weddingCode) {
      telemetry.weddingCodeBannerClicked(weddingCode);
    }
    // In-SPA navigation (accommodations is a guest-site route): keeps the SPA intact and lets the
    // click telemetry above flush, instead of being dropped by a full-page unload.
    history.push(accommodationsPath);
  }, [weddingCode, telemetry, accommodationsPath, history]);

  if (!isEligible || !weddingCode) {
    return null;
  }

  // Banner copy from the Figma design (node 54145-35770). The code renders in the canonical
  // `-AND-` format (e.g. EMMY-AND-JOHN) — the Figma's `EMMA-RYAN` is a placeholder.
  const message = tq`🏨 The couple has arranged guest lodging through Joy. Please book your room using group code ${weddingCode}.`;
  // Concise accessible name for the interactive banner — screen readers announce this instead of the
  // full marketing sentence.
  const ariaLabel = tq`Book your room with group code ${weddingCode}`;

  return (
    <>
      <WeddingCodeBannerSlot message={message} />
      <WeddingCodeBanner
        message={message}
        ariaLabel={ariaLabel}
        onNavigate={handleNavigate}
      />
    </>
  );
};

WeddingCodeBannerContainer.displayName = 'WeddingCodeBannerContainer';
